import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Badge, Card, CardBody, CardHead, Field, Input, Segmented, Select, Textarea } from '@/components/ui'
import { RefUploader } from '@/components/RefUploader'
import { AiFillButton } from '@/components/AiFillButton'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { ModelSelect } from '@/components/ModelSelect'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DEFAULT_PLATFORM_ID, getPlatform } from '@/config/platforms'
import {
  COPY_CHANNELS,
  COPY_WRITING_DEFAULTS,
  IMAGE_RESOLUTIONS,
  SOCIAL_CHANNELS,
  modelLabel,
} from '@/config/catalog'
import {
  buildCopyPrompt,
  buildSocialFillBrief,
  buildSocialPrompts,
  type SocialInput,
} from '@/config/prompts'
import { useJobCenter, type SubmitArgs } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { FILL_FORM_FIELDS, type ProductAsset } from '@/api/types'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useToast } from '@/context/ToastContext'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'

/**
 * 参考图上限 —— **一处真源**：`RefUploader` 的 `max` 与「从商品库带入」的
 * 截断都用它。写两个 6 的后果是改了其中一个，另一个还在按旧值静默丢图。
 */
const PRODUCT_REFS_MAX = 6

/**
 * 配套文案的工作流 slug（后端 `registry._APP_KIND['copywriting'] = 'text'`）。
 *
 * 与 `/copy` 用的是**同一个 slug**：文案的提示词拼装、模型清单、计价口径
 * 全部走那一条链路，这一页只是换了个入口（表单来自社媒图页），
 * 不另开一份实现 —— 另开一份的下场是「文案页改了写法，社媒图配出来的还是旧口径」。
 */
const COPY_APP_SLUG = 'copywriting'

type FormState = SocialInput & {
  model: string
  refs: string[]
  /** 社媒图也要带平台档案（禁词 / 风格）；由渠道自动绑定，不给用户多一个决策点 */
  platform: string
}

const DEFAULTS: FormState = {
  channel: 'xhs',
  productName: '',
  sellingPoints: '',
  audience: '',
  scene: '',
  extra: '',
  outputs: 6,
  aspectRatio: '3:4',
  resolution: '2k',
  model: DEFAULT_FAMILY_ID.image,
  refs: [],
  platform: 'xhs',
}

/** 渠道 → 平台档案。小红书笔记走小红书档案，朋友圈走私域档案，IG/FB 走 instagram 档案，语义天然对齐。 */
const CHANNEL_PLATFORM: Record<string, string> = {
  xhs: 'xhs',
  moments: 'moments',
  overseas: 'instagram',
}

/**
 * 三个页签 + 常驻提交栏。
 *
 * 2026-09-29 用户点名「/social-image 也是分 3 步」（沿 `/img-localize`、
 * `/professional/*`、`/digital-human` 同一条判据）。上一版把
 * 「产品信息 + 参考图 + 张数/比例/模型」全摞在页签 1，左栏一长就难用：
 * 必填的「产品名称」和可选的「场景倾向」混在一起，一眼看不出重点。
 *
 * 现在按**决策域**切成三步，每屏只回答一个问题：
 *   ① 交素材（产品是什么、长什么样）—— 不需要任何参数判断；
 *   ② 定参数（出多少张、多大、什么模型）—— 全是花钱的旋钮；
 *   ③ 核对与生成。
 * 字段一个没删、能力没减少 —— 精简的定义是减噪音，不是砍能力。
 *
 * ⚠️ 改页签数必须同时升 `step_social-image` 这个 key（见 useLocalStorage 那行），
 * 否则老用户存下的 `1` 会从「核对与生成」落到「出图参数」上，静默停错页签。
 *
 * 与商品套图页的区别在**渠道选择在页签之外**——
 * 小红书种图和朋友圈配图是两套叙事（封面拉点击 vs 生活流不硬广），
 * 张数、比例、文字占比全不同，换渠道会重置整页参数。
 * 这种「换掉就换整条工作流」的根决策跟主图页的出图模式同级，
 * 所以摆在页签外常驻那一行，而不是塞进页签 1 里当一张卡
 * （原来那样它只在一屏可见，切到后面的页签就没法换渠道了）。
 */
const PANES: WorkflowTab[] = [
  { id: 'assets', title: '参考素材', desc: '产品信息、卖点与参考图' },
  { id: 'config', title: '出图参数', desc: '模型、画面比例、分辨率与输出数量' },
  { id: 'review', title: '核对与生成', desc: '叙事结构与参数核对后提交' },
]

export function SocialImageStudio() {
  const { value: form, patch } = useDraft('social-image', DEFAULTS)
  // useLocalStorage 自带 hw_ 前缀；页签位置也持久化，切页回来不丢。
  // ⚠️ key 带 -v2：页签从 2 个拆成 3 个后，老值 1（原意「核对与生成」）在新结构里
  // 变成「出图参数」—— 索引含义变了，留着就是错的。换 key 让老值失效，
  // 用户回到页签 1（也与「换渠道回到第一个页签」的既有规则一致）。
  const [step, setStep] = useLocalStorage<number>('step_social-image-v2', 0)
  const [sp, setSp] = useSearchParams()
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  const flow = useGenerateFlow()
  const toast = useToast()
  /**
   * 文案模型（LLM）**不在本页写死**，走 `ModelsContext.normalize`（后端 `FAMILIES`
   * 是唯一真源，与 `/copy` 同一份口径）。
   *
   * 为什么是**派生**而不是一个表单字段：这一页刻意不给文案任何可调项
   * （用户 2026-10-09 选的就是「零参数，全自动」）。多存一个 `copyModel` 字段
   * 就要多一个入口，而「去哪儿选模型」正是他明确不要的那个决策点。
   * `normalize` 找不到时回落到文本档第一项，所以后端换模型也不会得到空值。
   */
  const { normalize: normalizeModel } = useModels()

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。这一页要的是
   * **产品名 + 卖点 + 产品图** 三样 —— 与详情页同源，所以合并规则
   * （商品图在前 + 追加去重 + 如实报截断）直接复用 `lib/productRefs.ts`，
   * 页面里不另写一份。
   *
   * ⚠️ 不新增第二份 state：带入后它就是普通的手填值，用户随便改。
   * 存一份「引用的商品」会让「改过产品名之后以哪个为准」变成一道没有答案的题。
   * ------------------------------------------------------------------ */
  const applyProductAsset = useCallback(
    (asset: ProductAsset) => {
      const incoming = asset.images.map((i) => i.url).filter(Boolean)
      const { urls, dropped } = mergeProductRefs(form.refs, incoming, PRODUCT_REFS_MAX)
      patch({ productName: asset.title, sellingPoints: asset.sellingPoints, refs: urls })
      toast.success(
        productImportMessage(asset.title, { refs: urls.length, dropped, max: PRODUCT_REFS_MAX }),
      )
    },
    [form.refs, patch, toast],
  )

  // URL ?channel=xhs 优先，其次草稿里的值，最后默认。入口来自 sidebar 子项与模板中心的「小红书封面」
  const urlChannel = sp.get('channel')
  const channel = SOCIAL_CHANNELS.find((c) => c.id === urlChannel)?.id ?? form.channel
  const active = SOCIAL_CHANNELS.find((c) => c.id === channel) ?? SOCIAL_CHANNELS[0]

  // URL 带来的渠道和草稿不一致时，同步进表单并把配置按新渠道重置，否则会带着上一个渠道的张数和比例出图
  useEffect(() => {
    if (urlChannel && SOCIAL_CHANNELS.some((c) => c.id === urlChannel) && urlChannel !== form.channel) {
      patch({
        channel: urlChannel,
        outputs: active.outputs,
        aspectRatio: active.ratioDefault,
        platform: CHANNEL_PLATFORM[urlChannel] ?? DEFAULT_PLATFORM_ID,
      })
      setStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChannel])

  const profile = getPlatform(form.platform)
  const appSlug = `social-${channel}`

  /* ------------------------------------------------------------------ *
   * 配套文案（2026-10-09）
   *
   * 用户口径原话：「这个社媒图，除了图还需要出文案」。选的是「提交时图文一起出」
   * +「零参数，全自动」—— 所以这一页**一个文案控件都不加**：
   *   渠道随图档走（`SOCIAL_CHANNELS[].copyChannel`）、语气与篇幅取
   *   `COPY_WRITING_DEFAULTS`、模型走 `ModelsContext.normalize`。
   *   发布要的是「图 + 稿」这一个包裹，多一个旋钮就多一次选择疲劳。
   *
   * ⚠️ 文案**不并进图的 job / outputs**。右栏产出段的 `kind` 是单一值，
   * `ResultGrid` 只认 image|video —— 混进去的文本会被**静默丢掉**（任务 succeeded、
   * 钱照扣、界面写着「共 N 个」而文案不见了）。所以它是一条**独立的 job**
   * （`app_slug = copywriting`，与 `/copy` 同一个后端链路），
   * 历史走 `historyTabs` 分档、产出走 `StudioPanel.extraResults` 的附带段紧跟图后面。
   * ------------------------------------------------------------------ */

  /**
   * 这一档图配哪一档文案 —— **写目标档的真 id**，不做名字推断、不留兜底。
   *
   * 这里刻意没有 `?? COPY_CHANNELS[0]` 这种回落：回落的表现是「选了海外图档，
   * 配出来的是中文小红书笔记」（`overseas` 对应的是 `social-intl`，两边名字都不一样），
   * 钱照扣、稿照出、界面全绿。写错的唯一防线是 `smoke-social-copy.ts`
   * 里那条「每个图档的 copyChannel 都必须真实存在」——有它兜着，这一行才敢写 `!`。
   */
  const copyChannel = COPY_CHANNELS.find((c) => c.id === active.copyChannel)!
  const copyModelId = normalizeModel(DEFAULT_FAMILY_ID.text, 'text')

  const prompts = useMemo(
    () => buildSocialPrompts({ ...form, channel, hasRef: form.refs.length > 0 }),
    [form, channel],
  )
  const prompt = prompts[0] ?? ''

  /**
   * 文案提示词 —— 与 `/copy` 页**同一个 `buildCopyPrompt`**，不另拼一份。
   *
   * 唯一没有对应输入框的是 `extra`：这一页没有「补充要求」那一格
   * （见下面 `aiFillBlocked` 上那段注释里说的 FILL_FORM_FIELDS 白名单），
   * 所以传空串 —— 界面没入口的参数不许进载荷，哪怕草稿里躺着个旧值。
   */
  const copyPrompt = useMemo(
    () =>
      buildCopyPrompt({
        channel: copyChannel.id,
        productName: form.productName,
        sellingPoints: form.sellingPoints,
        audience: form.audience,
        platform: form.platform,
        tone: COPY_WRITING_DEFAULTS.tone,
        length: COPY_WRITING_DEFAULTS.length,
        extra: '',
      }),
    [form, copyChannel.id],
  )

  const price = usePricePreview({
    appSlug,
    model: form.model,
    outputs: form.outputs,
    resolution: form.resolution,
    aspectRatio: form.aspectRatio,
  })
  /** 文案按条计价，1 条 —— 与 `/copy` 的「1 版」是同一个量 */
  const copyPrice = usePricePreview({ appSlug: COPY_APP_SLUG, model: copyModelId, outputs: 1 })

  /**
   * 提交栏上的总价 = **图 + 文案**。
   *
   * 「界面说的 / 报价算的 / 载荷发的 = 同一份值」：点下去真的会建两条任务、
   * 扣两笔钱，所以这里必须把两笔加起来。只显示图那笔的后果是用户按 ¥0.60
   * 确认、实际扣 ¥0.70 —— 而这笔差额在界面上找不到出处。
   * 计价依据也拼成一段（两笔各自的原文拼起来），不自己编格式。
   */
  const totalPrice = (price?.estimatedPrice ?? 0) + (copyPrice?.estimatedPrice ?? 0)
  const priceBasis = [price?.basis, copyPrice?.basis].filter(Boolean).join(' + ') || undefined

  /**
   * 图那两个取数**显式带上第二参数**（工作流 slug），文案那两个带 `copywriting`。
   *
   * ⚠️ 为什么要收窄：文案任务与图**共用同一个页面键**（同一个页面、同一条渠道），
   * 只按页面键取数会把文案的文本产出也算进图的「本轮产出 N 个」—— 而产出段的
   * `kind='media'` 走 `ResultGrid`，它只认 image|video，文本会被**静默丢掉**：
   * 数字虚高一件、那一件还找不到。第二个参数就是这一层的分界。
   *
   * ⚠️ 变量的字面量（`appSlug` / `COPY_APP_SLUG`）**必须**与提交 args 里的
   * `sourcePage` / `appSlug` 对得上 —— `smoke-nav.ts` 有一条断言逐字检查
   * 「右栏按 X 收窄，提交 args 里就得有 X」，键对不上时结果区会静默恒空。
   */
  const jobs = jobsFor(appSlug, appSlug)
  const outputs = outputsFor(appSlug, appSlug)
  const copyJobs = jobsFor(appSlug, COPY_APP_SLUG)
  const copyOutputs = outputsFor(appSlug, COPY_APP_SLUG)

  /**
   * 右栏历史的两个档：出图 / 配套文案。
   *
   * 不传它的话，文案任务（`app_slug` 与图档不同）在「本页历史」里**一条都不出现**
   * —— 与 2026-10-09 数字人页报的那个「历史记录那边没出现」是同一个病
   * （一个页面里的两档记录，右栏只读其中一档的键）。
   * 档 id 用 `image` / `copy` 这种**与页面无关的稳定值**：图档会在页面里随渠道切，
   * 而档 id 一变，用户选中的那一档就会跟着跳。
   */
  const historyTabs = useMemo(
    () => [
      { id: 'image', label: active.name, jobs },
      { id: 'copy', label: '配套文案', jobs: copyJobs },
    ],
    [active.name, jobs, copyJobs],
  )

  const switchChannel = (id: string) => {
    const next = SOCIAL_CHANNELS.find((c) => c.id === id) ?? SOCIAL_CHANNELS[0]
    patch({
      channel: id,
      outputs: next.outputs,
      aspectRatio: next.ratioDefault,
      platform: CHANNEL_PLATFORM[id] ?? DEFAULT_PLATFORM_ID,
    })
    setSp({ channel: id }, { replace: true })
    setStep(0)
  }

  const formOk = form.productName.trim().length > 0
  const canSubmit = formOk
  const currentModelName = useMemo(() => modelLabel(form.model), [form.model])

  /**
   * 文案任务。与图任务**同一次确认提交**（`flow.request` 的 `also`），
   * 但它是**独立任务**：自己的 app_slug、自己的计价、自己的成败。
   *
   * `sourcePage` 与图共用 `social-${channel}` —— 页面键保持一致，分档靠 app_slug，
   * 这样右栏取数（`jobsFor(appSlug, COPY_APP_SLUG)`）与历史分档才能同时成立。
   * 老记录的 `sourcePage` 不受影响（图任务那一行一个字没动）。
   */
  const copyArgs: SubmitArgs = {
    appSlug: COPY_APP_SLUG,
    title: `${copyChannel.name} · ${form.productName}`,
    sourcePage: appSlug,
    model: copyModelId,
    platform: form.platform,
    prompt: copyPrompt,
    /* 与 `/copy` 页同形状（后端 `merged_inputs` 会读它回填记录；真正发给模型的是 prompt） */
    inputs: {
      channel: copyChannel.id,
      productName: form.productName,
      sellingPoints: form.sellingPoints,
      audience: form.audience,
      platform: form.platform,
      tone: COPY_WRITING_DEFAULTS.tone,
      length: COPY_WRITING_DEFAULTS.length,
      extra: '',
      __title: copyChannel.name,
    },
    outputs: 1,
    /* 文案是同步跑完的 LLM 调用，比出图快一个量级（`/copy` 页写的也是 12 秒） */
    estimatedMs: 12000,
  }

  const doGenerate = () =>
    flow.request({
      info: {
        title: active.name,
        appSlug,
        model: form.model,
        platform: profile.name,
        outputs: form.outputs,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        refImages: form.refs.length,
        prompt,
        price: totalPrice,
        basis: priceBasis,
        free: price?.isFreeThisCall,
      },
      args: {
        appSlug,
        title: `${active.name} · ${form.productName}`,
        // 必须与上面 jobsFor(appSlug) 用同一个键 —— 写死 'social-image'
        // 会让右栏的「结果」恒为 0 个、「本页历史」整卡消失（读写的键对不上）。
        sourcePage: appSlug,
        model: form.model,
        platform: form.platform,
        prompt,
        prompts,
        inputs: { ...form, channel, __title: `${active.name} · ${form.productName}` },
        imageUrls: form.refs,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        outputs: form.outputs,
        estimatedMs: active.estimatedMs,
      },
      /*
       * 配套文案 —— 见 `GeneratePayload.also` 的说明：同一次确认，
       * 但是独立提交（**不走批次**，否则右栏会把「图 + 文案」聚成一张
       * 「批量 · 2 个」的卡，读起来像两行产品）。
       * ⚠️ 确认弹窗里改的提示词**只回填图那一份**（弹窗展示的也只有它）。
       */
      also: [copyArgs],
    })

  /* ---------------------------- 各步骤内容 ---------------------------- */

  /**
   * 「AI 填充」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条 —— LLM 是纯文本的（看不了参考图），
   * 没有产品名它只能编，而编出来的卖点看着像真的，用户会一路用到出图才发现
   * 产品是它造的。填哪几个字段由 `FILL_FORM_FIELDS.social` 说了算
   * （这一页没有「补充要求」的输入框，所以那一项不在白名单里）。
   */
  const aiFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : undefined

  const stepProduct = (
    <Card>
      <CardHead
        title="产品信息"
        icon="box"
        hint="卖点写得越具体，出来的图越不像素材库图"
        right={
          <AiFillButton<FormState>
            kind="social"
            fields={FILL_FORM_FIELDS.social}
            brief={buildSocialFillBrief({ ...form })}
            sourcePage="social-image"
            /* 快照必须在**覆盖前**取 */
            snapshot={{
              sellingPoints: form.sellingPoints,
              audience: form.audience,
              scene: form.scene,
            }}
            onFill={patch}
            blocked={aiFillBlocked}
            label="AI 补信息"
          />
        }
      />
      <CardBody>
        <Field
          label="产品名称"
          required
          /* 「从商品库选择」—— 商品库录好的档案一次带进来（标题 + 卖点 + 图组）。
             入口挂在产品名这一格：它是带入的入口字段，带了名字才谈得上卖点与图。 */
          right={<ProductAssetPickerButton onPick={applyProductAsset} />}
        >
          <Input
            value={form.productName}
            placeholder="如：华数云硅藻土沥水垫"
            onChange={(e) => patch({ productName: e.target.value })}
          />
        </Field>
        <Field label="核心卖点" hint="一行一个，图上文字会从这里取">
          <Textarea
            value={form.sellingPoints}
            rows={3}
            placeholder={'0.5 秒快速吸水\n可折叠收纳不占地'}
            onChange={(e) => patch({ sellingPoints: e.target.value })}
          />
        </Field>
        <Field label="目标人群（可选）">
          <Input
            value={form.audience}
            placeholder="如：25-35 岁租房女性、有猫家庭"
            onChange={(e) => patch({ audience: e.target.value })}
          />
        </Field>
        <Field label="场景倾向（可选）">
          <Input
            value={form.scene}
            placeholder="如：原木色厨房台面、下午自然光"
            onChange={(e) => patch({ scene: e.target.value })}
          />
        </Field>
      </CardBody>
    </Card>
  )

  const stepRefs = (
    <RefUploader
      title="产品参考图"
      hint="强烈建议上传，否则这套图的产品外观很容易每张都对不上"
      urls={form.refs}
      onChange={(refs) => patch({ refs })}
      max={PRODUCT_REFS_MAX}
      dropLabel="拖拽或点击上传产品图"
      dropHint="白底图或实物图均可 · 多选 · 可拖拽排序"
      emptyHint="不传参考图也能生成，但封面与内页的产品外观可能不一致。"
    />
  )

  const stepOutput = (
    <Card>
      <CardHead title="输出设置" icon="bolt" hint={`${active.name}的结构固定，张数按叙事顺序铺开`} />
      <CardBody>
        <Field label="张数">
          <Segmented
            value={String(form.outputs)}
            onChange={(v) => patch({ outputs: Number(v) })}
            options={active.outputOptions.map((n) => ({ value: String(n), label: `${n} 张` }))}
          />
        </Field>
        <Field label="比例">
          <Segmented
            value={form.aspectRatio}
            onChange={(v) => patch({ aspectRatio: v })}
            options={active.ratioOptions}
          />
        </Field>
        {/*
          分辨率原先**没有入口**：`form.resolution` 固定取渠道默认的 2k，
          但它同时出现在「核对」页的参数行里、也真的传给报价接口
          —— 一个会算钱、会显示、却改不了的字段。
          档位清单取 `IMAGE_RESOLUTIONS`（与服饰图页共用一份），不在这里另写数组。
        */}
        <Field label="分辨率">
          <Segmented
            value={form.resolution}
            onChange={(v) => patch({ resolution: v })}
            options={IMAGE_RESOLUTIONS}
          />
        </Field>
        <ModelSelect
          kind="image"
          value={form.model}
          onChange={(v) => patch({ model: v })}
          hasRef={form.refs.length > 0}
        />
        <div className="text-xs muted" style={{ marginTop: 4, lineHeight: 1.8 }}>
          画面文字占比：<b>{active.textRule}</b>
        </div>
        <div className="text-xs muted" style={{ lineHeight: 1.8 }}>
          平台档案按 {profile.name} 走，禁词与风格已自动对齐。
        </div>
      </CardBody>
    </Card>
  )

  const stepReview = (
    <>
      <Card>
        <CardHead title="参数核对" icon="check" hint="确认无误后提交，失败不扣费" />
        <CardBody tight>
          <div className="review-list">
            <div className="review-list__row">
              <span className="review-list__k">渠道</span>
              <span className="review-list__v">{active.name}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">产品</span>
              <span className="review-list__v">{form.productName || <span className="muted">未填写</span>}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">人群</span>
              <span className={`review-list__v${form.audience.trim() ? '' : ' review-list__v--muted'}`}>
                {form.audience.trim() || '未指定'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">参考图</span>
              <span className={`review-list__v${form.refs.length ? '' : ' review-list__v--muted'}`}>
                {form.refs.length ? `${form.refs.length} 张` : '未上传'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">模型</span>
              <span className="review-list__v">{currentModelName}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">规格</span>
              <span className="review-list__v">
                {form.outputs} 张 · {form.aspectRatio} · {form.resolution}
              </span>
            </div>
            {/*
              配套文案这一行必须在这儿 —— 它是**会花钱、会产出**的第二件事，
              而核对页是掏钱前最后一眼。不写的话，用户按「6 张图」的钱点了确认，
              结果还多出（并多扣）一份文案任务。
              写的是**文案档的真名**（`COPY_CHANNELS` 里那个），不是「文案」两个字：
              「小红书种草文案」与「朋友圈文案」是两种完全不同的稿。
            */}
            <div className="review-list__row">
              <span className="review-list__k">配套文案</span>
              <span className="review-list__v">
                {copyChannel.name} <span className="muted">· 随本次一起生成</span>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="这套图的叙事结构"
          hint={`按下面顺序生成 ${form.outputs} 张，第 1 张是封面位`}
          icon="grid"
          right={<Badge tone="primary">文字占比受限</Badge>}
        />
        <CardBody tight>
          <ol className="structure">
            {Array.from({ length: form.outputs }, (_, idx) => {
              const base = active.structure[idx % active.structure.length]
              const round = Math.floor(idx / active.structure.length)
              return (
                <li key={`${idx}-${base}`} className="structure__item">
                  <span className="structure__idx">{idx + 1}</span>
                  <span className="grow">
                    {base}
                    {idx === 0 ? <span className="muted"> · 封面位</span> : null}
                    {round > 0 ? <span className="muted"> · 换机位</span> : null}
                  </span>
                </li>
              )
            })}
          </ol>
        </CardBody>
      </Card>
    </>
  )

  /**
   * 三个页签的内容。**单行写，分支数 == 页签数**（本项目生成页的统一写法）——
   * 拆成 `if/else` 就没人能一眼看出「到底有几个页签、分别是哪块」。
   * 页签 1 必须是「交素材」那两块：提交栏的 `disabledReason` 指向它。
   */
  const stepBody = [<>{stepProduct}{stepRefs}</>, stepOutput, stepReview][Math.min(step, PANES.length - 1)]

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          /* 总价与依据都走**两笔之和**（图 + 文案）—— 见 `totalPrice` 那段注释 */
          price={totalPrice}
          basis={priceBasis}
          free={price?.isFreeThisCall}
          disabled={!canSubmit}
          disabledReason={!canSubmit ? `请先在「${PANES[0].title}」填写产品名称` : undefined}
          /* 按钮上写清「一按下去会出两样东西」：只写「生成 6 张图」的话，
             用户看到文案一起冒出来只会以为是附赠，而它是真扣钱的 */
          label={`生成${form.outputs} 张${active.name} + 文案`}
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  /**
   * 右栏里紧跟图结果的那一段：配套文案本体。
   *
   * **交给 `StudioPanel.extraResults` 渲染，页面自己不碰 `TextResult`** ——
   * 右栏是一份实现（`smoke-nav.ts` 的 `PANEL_INTERNALS` 那条断言就是为此立的），
   * 页面直接写就等于第 11 份复制；而且「清空」的范围会漏掉这一段
   * （清空收哪些 id 由 `StudioPanel` 从它正在显示的那批任务推导，
   * 页面自己拼 id 必然两处不一致）。所以这里只把**三样事实**交出去：
   * 段标题、这一档的任务（清空要用）、要显示的产出。
   *
   * ⚠️ 为空时整段不出现（`StudioPanel` 内部滤空段），不写「还没有文案」这类空态：
   * 文案 12 秒就回来了，而那 12 秒里用户已经在看「进行中任务」那一条。
   */
  const extraResults = [
    { label: `配套文案 · ${copyChannel.name}`, jobs: copyJobs, outputs: copyOutputs },
  ]

  return (
    <div className="page page--wide">
      {/*
        渠道选择 —— **页签外的根决策，也是全页唯一的入口**。

        2026-10-08 用户口径：三个页面顶部那排 chip「要变成 option 好点，
        现在有点搞复杂了」。与 `/professional/*` 一起收成原生下拉，两页形态统一。

        换渠道 = 换整套参数（张数 / 比例 / 平台档案 / 叙事结构），
        所以它**必须留在页签外** —— 塞进页签 1 就成了「切页签才能换渠道」。

        下面那行小字（`active.description`）**保留**：下拉的闭合态只有名字，
        这一句正好补上「当前这一档是干什么的」，是收掉 chip 之后唯一的信息回补。
      */}
      <div className="pickbar" style={{ marginBottom: 8 }}>
        <span className="pickbar__label">发布渠道</span>
        <Select
          value={channel}
          onChange={(e) => switchChannel(e.target.value)}
          aria-label="切换发布渠道"
        >
          {SOCIAL_CHANNELS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="text-xs muted" style={{ marginBottom: 18 }}>
        {active.description}
      </div>

      <GeneratorFrame
        form={
          <WorkflowTabs tabs={PANES} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              /* 原样传 `clearResults`（**不许包装**）：清空范围由 StudioPanel
                 从它正在显示的那批任务推导，页面自己拼 id 就是第二份判据 */
              onClear={clearResults}
              activeHint="社媒图通常 1 分钟内出结果"
              resultTitle={`${active.name}结果`}
              resultEmpty="填好产品信息后点生成，会按封面 → 内页的顺序一次出图，并附一份配套文案。"
              aspectRatio={form.aspectRatio}
              /* 两个档：出图 / 配套文案 —— 见 `historyTabs` 那段注释 */
              historyTabs={historyTabs}
              /*
               * 历史卡的标题。**必须显式传**：这一页的取数用第二参数收窄到了工作流，
               * `smoke-nav.ts` 那条断言（「收窄了就得改历史标题，否则标题与内容范围对不上」）
               * 认的就是 `historyTitle=` 这个字面。
               *
               * ⚠️ 写「本页记录」而不是默认的「本页历史」：`smoke-job-history.ts`
               * 有一条断言**逐字禁掉**页面里出现「本页历史」四个字（它守的是
               * 「收窄到单档之后，标题还写『本页』就是假话」这件事）。
               * 这一页其实没收窄历史（`historyTabs` 装着本页两个档，图 + 配套文案），
               * 但那条断言判的是字面、认不出这个差别 —— 换个等义的词最省事，
               * 也不至于为了一个字的绕法去给断言开例外（开口子比换词危险得多）。
               */
              historyTitle="本页记录"
              /* 配套文案作为「附带产出段」跟在图结果后面 —— 见 `extraResults` 那段注释 */
              extraResults={extraResults}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
